// 读取图片“真实像素尺寸”：只解析文件头的少量字节，不下载整图，也不依赖任何第三方服务。
//
// 背景：画布上展示的是 Supabase 转换后的缩略图（例如高 512），其 naturalWidth/Height 并非原图尺寸，
// 直接用 <img onLoad> 记录会把缩略图尺寸误记为真实尺寸。这里改为对“原图 URL”做一次流式请求，
// 读到能解析出尺寸的字节后立即 cancel，从而以极小流量拿到真实分辨率。
//
// 支持：PNG / JPEG / GIF / WebP / BMP。解析不出时返回 null，由调用方决定是否兜底。

export type ImageSize = { width: number; height: number };

function readUInt16BE(b: Uint8Array, off: number): number {
  return (b[off] << 8) | b[off + 1];
}
function readUInt16LE(b: Uint8Array, off: number): number {
  return b[off] | (b[off + 1] << 8);
}
function readUInt32BE(b: Uint8Array, off: number): number {
  return (
    ((b[off] << 24) >>> 0) +
    (b[off + 1] << 16) +
    (b[off + 2] << 8) +
    b[off + 3]
  );
}
function readUInt32LE(b: Uint8Array, off: number): number {
  return (
    b[off] +
    (b[off + 1] << 8) +
    (b[off + 2] << 16) +
    ((b[off + 3] << 24) >>> 0)
  );
}

/** 尝试从已累积的字节里解析图片尺寸；信息不足时返回 null（等待更多字节）。 */
export function parseImageSize(b: Uint8Array): ImageSize | null {
  const len = b.length;
  if (len < 4) return null;

  // PNG: 8 字节签名 + IHDR(宽 @16, 高 @20, big-endian)
  if (
    b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47
  ) {
    if (len < 24) return null;
    const width = readUInt32BE(b, 16);
    const height = readUInt32BE(b, 20);
    return width > 0 && height > 0 ? { width, height } : null;
  }

  // GIF: 'GIF8' + 宽 @6, 高 @8 (little-endian)
  if (b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x38) {
    if (len < 10) return null;
    const width = readUInt16LE(b, 6);
    const height = readUInt16LE(b, 8);
    return width > 0 && height > 0 ? { width, height } : null;
  }

  // BMP: 'BM' + 宽 @18, 高 @22 (little-endian, 高可能为负表示自上而下)
  if (b[0] === 0x42 && b[1] === 0x4d) {
    if (len < 26) return null;
    const width = readUInt32LE(b, 18);
    let height = readUInt32LE(b, 22);
    if (height > 0x7fffffff) height = 0x100000000 - height; // 处理负数（顶到底）
    return width > 0 && height > 0 ? { width, height } : null;
  }

  // WebP: 'RIFF'....'WEBP' + 块类型(VP8 / VP8L / VP8X)
  if (
    b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 &&
    len >= 16 &&
    b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50
  ) {
    const fourcc = String.fromCharCode(b[12], b[13], b[14], b[15]);
    if (fourcc === 'VP8 ') {
      // 简单有损：在 0x14 处有 0x9d 0x01 0x2a 同步码，随后是 16-bit 宽/高(低 14 位)
      if (len < 30) return null;
      const width = readUInt16LE(b, 26) & 0x3fff;
      const height = readUInt16LE(b, 28) & 0x3fff;
      return width > 0 && height > 0 ? { width, height } : null;
    }
    if (fourcc === 'VP8L') {
      // 无损：0x14 为签名 0x2f，随后 4 字节里打包 14-bit(宽-1) 与 14-bit(高-1)
      if (len < 25 || b[20] !== 0x2f) return null;
      const bits =
        b[21] | (b[22] << 8) | (b[23] << 16) | (b[24] << 24);
      const width = (bits & 0x3fff) + 1;
      const height = ((bits >> 14) & 0x3fff) + 1;
      return width > 0 && height > 0 ? { width, height } : null;
    }
    if (fourcc === 'VP8X') {
      // 扩展：画布(宽-1) @24 (24-bit LE)，(高-1) @27 (24-bit LE)
      if (len < 30) return null;
      const width = (b[24] | (b[25] << 8) | (b[26] << 16)) + 1;
      const height = (b[27] | (b[28] << 8) | (b[29] << 16)) + 1;
      return width > 0 && height > 0 ? { width, height } : null;
    }
    return null;
  }

  // JPEG: FF D8 起；逐段扫描，遇到 SOF(0xC0-0xCF, 除 C4/C8/CC) 读宽高(big-endian)
  if (b[0] === 0xff && b[1] === 0xd8) {
    let off = 2;
    while (off + 9 < len) {
      if (b[off] !== 0xff) {
        off++;
        continue;
      }
      let marker = b[off + 1];
      // 跳过填充字节 0xFF
      while (marker === 0xff && off + 1 < len) {
        off++;
        marker = b[off + 1];
      }
      // 无段体的标记（RSTn / SOI / EOI / TEM）
      if (
        marker === 0xd8 || marker === 0xd9 ||
        (marker >= 0xd0 && marker <= 0xd7) || marker === 0x01
      ) {
        off += 2;
        continue;
      }
      if (off + 4 >= len) return null;
      const segLen = readUInt16BE(b, off + 2);
      // SOF0..SOF15，排除 DHT(C4)/JPG(C8)/DAC(CC)
      if (
        marker >= 0xc0 && marker <= 0xcf &&
        marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc
      ) {
        if (off + 9 >= len) return null;
        const height = readUInt16BE(b, off + 5);
        const width = readUInt16BE(b, off + 7);
        return width > 0 && height > 0 ? { width, height } : null;
      }
      off += 2 + segLen;
    }
    return null;
  }

  return null;
}

/**
 * 流式读取 URL 指向的图片，解析出真实像素尺寸。读到尺寸即取消下载，最多读取 maxBytes。
 * 失败（网络错误 / 不支持 / 字节不足 / 未知格式）返回 null。
 */
export async function readImageSizeFromUrl(
  url: string,
  opts?: { signal?: AbortSignal; maxBytes?: number },
): Promise<ImageSize | null> {
  const maxBytes = opts?.maxBytes ?? 512 * 1024;
  let res: Response;
  try {
    res = await fetch(url, { signal: opts?.signal, cache: 'force-cache' });
  } catch {
    return null;
  }
  if (!res.ok || !res.body) return null;

  const reader = res.body.getReader();
  let buf = new Uint8Array(0);
  try {
    while (buf.length < maxBytes) {
      const { done, value } = await reader.read();
      if (value && value.length) {
        const merged = new Uint8Array(buf.length + value.length);
        merged.set(buf, 0);
        merged.set(value, buf.length);
        buf = merged;
        const size = parseImageSize(buf);
        if (size) return size;
      }
      if (done) break;
    }
  } catch {
    // ignore：返回已尽力解析的结果
  } finally {
    try {
      await reader.cancel();
    } catch {}
  }
  return parseImageSize(buf);
}
